/**
 * pwaInstall — singleton that captures the `beforeinstallprompt` event at
 * app boot so any component (button, banner, settings link) can later fire
 * the OS-level install dialog on user click.
 *
 * Why a module-level listener instead of per-component:
 *   - The event fires exactly once per page load, very early (often before
 *     React even mounts). A component-level listener registered inside a
 *     useEffect can miss it entirely — especially in Brave/Firefox where
 *     timing is less forgiving than Chrome.
 *   - We need the same captured event accessible from both InstallAppButton
 *     and InstallPwaPrompt. A singleton avoids two listeners fighting over
 *     the same event.
 *
 * Usage:
 *   import './lib/pwaInstall';                       // side-effect: registers listener
 *   import { promptInstall } from './lib/pwaInstall';
 *   const outcome = await promptInstall();           // 'accepted' | 'dismissed' | null
 *   if (outcome === null) {  ... no deferred prompt available, show fallback }
 */

// The captured event. Survives across React mounts because this module is
// only evaluated once.
let deferredPrompt = null;

// Listeners that want to know when the deferred prompt arrives or is
// consumed (e.g., InstallPwaPrompt re-renders to enable its native button).
const subscribers = new Set();

const notify = () => {
  for (const fn of subscribers) {
    try { fn(deferredPrompt); } catch { /* swallow subscriber errors */ }
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Per spec: preventDefault() stops the browser from auto-showing its
    // mini-infobar. We want to choose when to surface the prompt.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });

  // When the user completes install (via our button OR the browser's own
  // address-bar icon), clear our state so the UI hides itself.
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export const getDeferredPrompt = () => deferredPrompt;

export const subscribeDeferredPrompt = (fn) => {
  subscribers.add(fn);
  // Push the current value once so late subscribers get an immediate snapshot.
  try { fn(deferredPrompt); } catch { /* ignore */ }
  return () => subscribers.delete(fn);
};

/**
 * Fire the OS install dialog if a deferred prompt is available.
 * SYNCHRONOUS up to the `.prompt()` call so it preserves the user-activation
 * context — browsers (esp. Brave/Safari) reject `.prompt()` if it's called
 * after any `await` boundary inside the click handler.
 *
 * Returns:
 *   - `null` if no prompt was captured (Safari, Firefox, Brave w/ shields).
 *     Caller should fall back to address-bar guidance.
 *   - `Promise<'accepted' | 'dismissed'>` resolving with the user's choice
 *     after they interact with the native dialog.
 */
export const promptInstall = () => {
  if (!deferredPrompt) return null;
  const p = deferredPrompt;
  try {
    // Critical: fire synchronously, BEFORE clearing state. The prompt() call
    // must happen inside the user-gesture window or browsers silently no-op.
    p.prompt();
  } catch {
    // Some browsers throw if prompt() is called outside activation or twice.
    // Clear state so retries try the fallback path instead of looping.
    deferredPrompt = null;
    notify();
    return null;
  }
  // beforeinstallprompt can only be used once — clear *after* successfully
  // dispatching so subscribers see a consistent transition.
  deferredPrompt = null;
  notify();
  return p.userChoice.then(
    (choice) => choice?.outcome || 'dismissed',
    () => 'dismissed'
  );
};
